// The task change log (public.task_change_log, filled by a trigger on tasks — see
// supabase/migrations/20261018T120000Z_task_change_log.sql). Every task change is
// a row with the before/after of the columns that changed and who made it.
// BALANCE tags its agents' rows with the council session + speaker, and Joel
// resolves each: Approve, Revert (puts the before-values back) or Change (his own
// day + time). Nothing is ever deleted from the log.
import { supabase } from '../lib/supabase';
import { seatName } from './useCouncil';

export type ChangeStatus = 'pending' | 'approved' | 'reverted' | 'edited';
export interface TaskChange {
  id: string;
  task_id: string;
  task_title: string | null;
  op: 'insert' | 'update' | 'delete';
  changed: string[];
  before: Record<string, unknown> | null;
  after: Record<string, unknown> | null;
  actor: 'agent' | 'user' | 'system';
  session_id: string | null;
  speaker: string | null;
  status: ChangeStatus;
  note: string | null;
  created_at: string;
  resolved_at: string | null;
}

const COLS = 'id, task_id, task_title, op, changed, before, after, actor, session_id, speaker, status, note, created_at, resolved_at';

/** Changes made FOR the user (agents + direct system edits), newest first. */
export async function listChanges(userId: string, opts: { sessionId?: string; limit?: number } = {}): Promise<TaskChange[]> {
  let q = supabase.from('task_change_log').select(COLS).eq('user_id', userId).in('actor', ['agent', 'system']).order('created_at', { ascending: false }).limit(opts.limit ?? 200);
  if (opts.sessionId) q = q.eq('session_id', opts.sessionId);
  const { data } = await q;
  return (data as TaskChange[] | null) ?? [];
}

/** Claim untagged agent rows written since `since` for this BALANCE session + speaker. */
export async function tagChanges(userId: string, sessionId: string, speaker: string, since: string): Promise<void> {
  await supabase
    .from('task_change_log')
    .update({ session_id: sessionId, speaker })
    .eq('user_id', userId)
    .eq('actor', 'agent')
    .is('session_id', null)
    .gte('created_at', since);
}

async function resolve(id: string, status: ChangeStatus, note?: string): Promise<string | null> {
  const patch: Record<string, unknown> = { status, resolved_at: new Date().toISOString() };
  if (note) patch.note = note;
  const { error } = await supabase.from('task_change_log').update(patch).eq('id', id);
  return error?.message ?? null;
}

export function approveChange(c: TaskChange) {
  return resolve(c.id, 'approved');
}

/** Put the task back exactly as it was before this change. */
export async function revertChange(userId: string, c: TaskChange): Promise<string | null> {
  if (c.op === 'update') {
    const patch: Record<string, unknown> = {};
    for (const k of c.changed) patch[k] = c.before?.[k] ?? null;
    patch.updated_at = new Date().toISOString();
    const { error } = await supabase.from('tasks').update(patch).eq('id', c.task_id).eq('user_id', userId);
    if (error) return error.message;
  } else if (c.op === 'insert') {
    // An agent created this task: take it out of the lists (kept, not destroyed).
    const { error } = await supabase.from('tasks').update({ unlisted: true, updated_at: new Date().toISOString() }).eq('id', c.task_id).eq('user_id', userId);
    if (error) return error.message;
  } else if (c.op === 'delete' && c.before) {
    const { error } = await supabase.from('tasks').insert(c.before);
    if (error) return error.message;
  }
  return resolve(c.id, 'reverted');
}

/** Joel's own day (+ optional time) instead of the agent's. */
export async function editChangeSchedule(userId: string, c: TaskChange, date: string, time: string): Promise<string | null> {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) return 'Date must be YYYY-MM-DD.';
  if (time && !/^\d{2}:\d{2}$/.test(time)) return 'Time must be HH:MM.';
  const scheduled = time ? `${date} ${time}` : date;
  const { error } = await supabase
    .from('tasks')
    .update({ scheduled, scheduled_manual: true, tabled: false, updated_at: new Date().toISOString() })
    .eq('id', c.task_id)
    .eq('user_id', userId);
  if (error) return error.message;
  return resolve(c.id, 'edited', `Joel set it to ${scheduled}.`);
}

// ── Plain-language description ──────────────────────────────────────────────
const fmtDay = (v: unknown) => {
  if (v == null || v === '') return 'none';
  const s = String(v);
  const m = /^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{2}:\d{2}))?/.exec(s);
  if (!m) return s;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  const day = d.toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short' });
  return m[4] ? `${day} ${m[4]}` : day;
};
const yesNo = (v: unknown) => (v ? 'yes' : 'no');
const LABEL: Record<string, string> = { scheduled: 'Day', done: 'Done', tabled: 'Tabled', title: 'Title', list_id: 'List', category: 'Section', unlisted: 'Hidden', notes: 'Notes', priority_add: 'Priority', group_id: 'Group', scheduled_manual: 'Pinned' };

export function describeChange(c: TaskChange): string[] {
  if (c.op === 'insert') return ['Created this task'];
  if (c.op === 'delete') return ['Deleted this task'];
  return c.changed
    .filter((k) => k !== 'scheduled_manual')
    .map((k) => {
      const b = c.before?.[k];
      const a = c.after?.[k];
      if (k === 'scheduled') return `Day: ${fmtDay(b)} → ${fmtDay(a)}`;
      if (typeof a === 'boolean' || typeof b === 'boolean') return `${LABEL[k] ?? k}: ${yesNo(b)} → ${yesNo(a)}`;
      if (k === 'notes') return 'Notes changed';
      return `${LABEL[k] ?? k}: ${b ?? 'none'} → ${a ?? 'none'}`;
    });
}

export const whoMade = (c: TaskChange) => (c.speaker === 'claude' ? 'Claude' : c.speaker ? seatName(c.speaker) : c.actor === 'agent' ? 'An agent' : 'System');

// ── BALANCE conversations ───────────────────────────────────────────────────
export interface BalanceSession {
  id: string;
  title: string;
  created_at: string;
  updated_at: string;
  members: string[];
  muted: string[];
  status: 'active' | 'paused' | 'closed';
  lines: number;
  preview: string;
}

export async function listBalanceSessions(userId: string): Promise<BalanceSession[]> {
  const { data } = await supabase
    .from('council_sessions')
    .select('id, title, created_at, updated_at, members, muted, status')
    .eq('user_id', userId)
    .ilike('title', 'Balance%')
    .order('created_at', { ascending: false })
    .limit(60);
  const rows = (data as Omit<BalanceSession, 'lines' | 'preview'>[] | null) ?? [];
  if (!rows.length) return [];
  const { data: msgs } = await supabase
    .from('council_messages')
    .select('session_id, speaker, content, created_at')
    .in('session_id', rows.map((r) => r.id))
    .order('created_at', { ascending: true })
    .limit(2000);
  const by = new Map<string, { n: number; last: string }>();
  for (const m of (msgs as { session_id: string; speaker: string; content: string }[] | null) ?? []) {
    const e = by.get(m.session_id) ?? { n: 0, last: '' };
    e.n++;
    if (m.speaker !== 'user') e.last = `${seatName(m.speaker)}: ${m.content}`;
    by.set(m.session_id, e);
  }
  return rows.map((r) => ({ ...r, lines: by.get(r.id)?.n ?? 0, preview: by.get(r.id)?.last ?? '' }));
}
